import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { VideoGenerationRequestsTable } from "./VideoGenerationRequestsTable";
import { VideoGenerationDialogue } from "./VideoGenerationDialogue";
import { useAssets } from "../../../modules/perf_reports/api/assets/useAssets";
import { useAppSelector } from "../../../redux/hooks";
import AssetSelector from "../../core/blocks/AssetSelector/AssetSelector";
import Loading from "../../core/blocks/Loading";
import { useGetAiVideoGenRequests } from "../../../modules/perf_reports/api/assets/useGetAiVideoGenTaskResults";

type Props = {
  productId: number;
  asin: string;
};

export const VideoGeneration = ({ productId, asin }: Props) => {
  const { t } = useTranslation(["advertising"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const defaultFilters = useMemo(() => {
    return { tags: `${asin.toLowerCase()}, trellis generative-ai ${asin.toLowerCase()}` };
  }, [asin]);

  const videoAssetsQuery = useAssets({
    type: "VIDEO",
    marketplace: marketPlace,
    tags: asin.toLowerCase(),
  });

  const generatedAssets = videoAssetsQuery.data?.results.slice(0, 10) ?? [];

  const requestsFilters = {
    task__gvproduct__asin: asin,
    state: ["PENDING", "IN_PROGRESS", "SUCCESS", "FAILED"],
  };
  const videoGenerationRequestsQuery = useGetAiVideoGenRequests(requestsFilters);
  const videoGenerationRequests = videoGenerationRequestsQuery.data?.results ?? [];

  return (
    <VStack>
      <h3 className={css({ display: "flex", gap: "large", fontWeight: 400, textAlign: "center" })}>
        {t(($) => $.advertising["AI Video Generation"])}{" "}
      </h3>
      {videoGenerationRequests.length ?
        <VStack className={css({ paddingY: "large" })}>
          <h4 className={css({ marginY: "medium", fontSize: "medium", fontWeight: "medium" })}>
            {t(($) => $.advertising["Video Generation Requests"], { count: videoGenerationRequests.length })}
          </h4>
          <VideoGenerationRequestsTable requestsData={videoGenerationRequests} />
        </VStack>
      : null}
      <VStack className={css({ paddingY: "large" })}>
        {videoAssetsQuery.isLoading ?
          <Flex justifyContent={"center"} minHeight={"300px"} paddingY={"large"}>
            <Loading fullPage={false} height={"300px"} />
          </Flex>
        : generatedAssets.length ?
          <>
            <VideoGenerationDialogue asin={asin} productId={productId} />
            <AssetSelector
              overrideAssetData={generatedAssets}
              marketPlace={marketPlace}
              hideSelectionControls={true}
              hidePreviewField={true}
              hideAssetTypeFilter={true}
              hideEligibilityFilter={true}
              hideTagsFilter={true}
              hideAdvancedFilters={true}
              hideGeneratedAssetsFilter={true}
              defaultPageSize={500}
              defaultFilters={defaultFilters}
              assetGenerationSelectorType={"video"}
              initialTypeFilter={"VIDEO"}
            />
          </>
        : <>
            <p
              className={css({
                width: { base: "100%", lg: "70%" },
                marginY: "large",
                fontSize: "medium",
                textAlign: "center",
              })}
            >
              {t(
                ($) =>
                  $.advertising[
                    "There are currently no generated video assets registered on Amazon for this product. Click the button below to start generating video assets that can be used in Sponsored Brand media plans leveraging the power of AI. Once a request to generate assets is made, it typically takes around 10-15 minutes for the assets to be generated. Once complete, you will receive a notification in the top navigation bar with links to review the generated videos and select which ones to register to your Amazon asset library by clicking the 'Assets Ready for Review' tag under the 'Actions' column. Please note that generated assets must be registered within 6 hours of completion."
                  ],
              )}
            </p>
            <VideoGenerationDialogue asin={asin} productId={productId} />
          </>
        }
      </VStack>
    </VStack>
  );
};
